import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { selectSelectedAccount, selectSelectedCompanyDocument, useAppSelector, useGetOrganizationOwnerContactQuery, } from '@ibk/shared-config';
import { useTranslation } from '@ibk/shared-translation';
import { Button, Dropdown, Input } from '@ibk/shared-ui';
import { formatAsPhoneNumber } from '@ibk/shared-utils';
import { useCreateContactRequestMutation } from '../../../../services/benefitApi';
import { useCreateEmissorContactRequestByDomain } from '../../../../services/emissorBenefitsApi';
import { withOptionalCompanyIdentity } from '../../../../types';
import { INSIDE_SALES_BRAZIL_UF_OPTIONS } from '../../insideSalesBrazilUfOptions';
import styles from './InsideSalesContactRequestSection.module.css';
const EMPTY_FORM_VALUES = {
    fullName: '',
    email: '',
    phone: '',
    stateCode: undefined,
    city: '',
    collaboratorCount: '',
};
function normalizeContactPhoneDigits(raw) {
    let d = raw.replace(/\D/g, '');
    if (d.startsWith('55') && d.length > 11) {
        d = d.slice(2);
    }
    return d.slice(0, 11);
}
function formValuesFromOwner(owner) {
    return {
        fullName: owner.fullName ?? '',
        email: owner.email ?? '',
        phone: normalizeContactPhoneDigits(owner.phoneNumber ?? ''),
        stateCode: owner.state ?? undefined,
        city: owner.city ?? '',
        collaboratorCount: '',
    };
}
function validateCollaboratorCountValue(value, invalidMessage) {
    const n = Number.parseInt(String(value).trim(), 10);
    if (!Number.isNaN(n) && n >= 1) {
        return true;
    }
    return invalidMessage;
}
function validatePhoneDigits(value, phoneInvalidMsg) {
    const d = normalizeContactPhoneDigits(String(value ?? ''));
    if (d.length >= 10 && d.length <= 11) {
        return true;
    }
    return phoneInvalidMsg;
}
function InsideSalesContactIntro() {
    const { t } = useTranslation();
    return (_jsxs("div", { className: styles.intro, children: [_jsxs("h2", { className: styles.headline, children: [_jsx("span", { className: styles.headlineLine, children: t('benefits.inside-sales.contact.headline.line1') }), _jsxs("span", { className: styles.headlineLine, children: [t('benefits.inside-sales.contact.headline.line2Before'), _jsx("span", { className: styles.headlineHighlight, children: t('benefits.inside-sales.contact.headline.highlight') }), t('benefits.inside-sales.contact.headline.line2After')] })] }), _jsx("p", { className: styles.subtitle, children: t('benefits.inside-sales.contact.subtitle') })] }));
}
function InsideSalesContactFormFields({ onSubmit, fullNameField, emailField, cityField, collaboratorsField, control, errors, requiredMsg, stateRequiredMsg, phoneInvalidMsg, isSubmitDisabled, isSubmittingContact, }) {
    const { t } = useTranslation();
    const submitButtonClassName = isSubmittingContact
        ? `${styles.submitBtn} ${styles.submitBtnLoading}`
        : styles.submitBtn;
    const collaboratorsInputContainerClass = `${styles.landingInputBox} ${styles.numberFieldInput}`;
    return (_jsx("form", { className: styles.form, onSubmit: onSubmit, noValidate: true, children: _jsxs("div", { className: styles.formGrid, children: [_jsx("div", { className: styles.landingField, children: _jsx(Input, { id: 'inputInsideSalesContactFullName', size: 'large', dataTestId: 'inputInsideSalesContactFullName', label: t('benefits.inside-sales.contact.fullName'), inputRef: fullNameField.ref, name: fullNameField.name, onChange: fullNameField.onChange, onBlur: fullNameField.onBlur, error: errors.fullName?.message, className: styles.landingInputControl, containerClassName: styles.landingInputBox }) }), _jsx("div", { className: styles.landingField, children: _jsx(Input, { id: 'inputInsideSalesContactEmail', size: 'large', dataTestId: 'inputInsideSalesContactEmail', type: 'email', label: t('benefits.inside-sales.contact.email'), inputRef: emailField.ref, name: emailField.name, onChange: emailField.onChange, onBlur: emailField.onBlur, error: errors.email?.message, className: styles.landingInputControl, containerClassName: styles.landingInputBox }) }), _jsx(Controller, { name: 'phone', control: control, rules: {
                        required: requiredMsg,
                        validate: v => validatePhoneDigits(v, phoneInvalidMsg),
                    }, render: ({ field, fieldState }) => (_jsx("div", { className: styles.landingField, children: _jsx(Input, { id: 'inputInsideSalesContactPhone', size: 'large', dataTestId: 'inputInsideSalesContactPhone', type: 'tel', inputMode: 'numeric', autoComplete: 'tel', maxLength: 15, label: t('benefits.inside-sales.contact.phone'), name: field.name, inputRef: field.ref, value: formatAsPhoneNumber(field.value), onChange: e => {
                                field.onChange(normalizeContactPhoneDigits(e.target.value));
                            }, onBlur: field.onBlur, error: fieldState.error?.message, className: styles.landingInputControl, containerClassName: styles.landingInputBox }) })) }), _jsx(Controller, { name: 'stateCode', control: control, rules: { required: stateRequiredMsg }, render: ({ field, fieldState }) => (_jsx("div", { className: styles.landingField, children: _jsx(Dropdown, { label: t('benefits.inside-sales.contact.state'), options: INSIDE_SALES_BRAZIL_UF_OPTIONS, value: field.value, onChange: field.onChange, placeholder: t('benefits.inside-sales.contact.statePlaceholder'), dataTestId: 'dropdownInsideSalesContactUf', size: 'large', error: !!fieldState.error, errorMsg: fieldState.error?.message, className: styles.landingUfTrigger, containerClassName: styles.landingDropdownRoot }) })) }), _jsx("div", { className: styles.landingField, children: _jsx(Input, { id: 'inputInsideSalesContactCity', size: 'large', dataTestId: 'inputInsideSalesContactCity', label: t('benefits.inside-sales.contact.city'), inputRef: cityField.ref, name: cityField.name, onChange: cityField.onChange, onBlur: cityField.onBlur, error: errors.city?.message, className: styles.landingInputControl, containerClassName: styles.landingInputBox }) }), _jsxs("div", { className: styles.collabSubmit, children: [_jsx("div", { className: styles.landingField, children: _jsx(Input, { id: 'inputInsideSalesContactCollaborators', size: 'large', dataTestId: 'inputInsideSalesContactCollaborators', type: 'number', min: 1, label: t('benefits.inside-sales.contact.collaborators'), inputRef: collaboratorsField.ref, name: collaboratorsField.name, onChange: collaboratorsField.onChange, onBlur: collaboratorsField.onBlur, error: errors.collaboratorCount?.message, className: styles.landingInputControl, containerClassName: collaboratorsInputContainerClass }) }), _jsx(Button, { type: 'submit', variant: 'outline', size: 'large', disabled: isSubmitDisabled, dataTestId: 'buttonInsideSalesContactSubmit', className: submitButtonClassName, children: isSubmittingContact
                                ? t('benefits.inside-sales.contact.sending')
                                : t('benefits.inside-sales.contact.submit') })] })] }) }));
}
function InsideSalesContactFeedbackPanel({ variant, onAction, }) {
    const { t } = useTranslation();
    const isOk = variant === 'ok';
    const panelClassParts = [styles.feedbackPanel];
    if (!isOk) {
        panelClassParts.push(styles.feedbackPanelError);
    }
    const panelClassName = panelClassParts.join(' ');
    const title = isOk
        ? t('benefits.inside-sales.contact.feedback.successTitle')
        : t('benefits.inside-sales.contact.feedback.errorTitle');
    const body = isOk
        ? t('benefits.inside-sales.contact.feedback.successBody')
        : t('benefits.inside-sales.contact.feedback.errorBody');
    const actionLabel = isOk
        ? t('benefits.inside-sales.contact.feedback.successAction')
        : t('benefits.inside-sales.contact.feedback.errorAction');
    const dataTestId = isOk ? 'buttonInsideSalesContactFeedbackEditForm' : 'buttonInsideSalesContactTryAgain';
    const regionTestId = isOk ? 'viewInsideSalesContactFeedbackSuccess' : 'viewInsideSalesContactFeedbackError';
    const inner = (_jsxs(_Fragment, { children: [_jsxs("div", { className: styles.feedbackCopy, children: [_jsx("h3", { className: styles.feedbackTitle, children: title }), _jsx("p", { className: styles.feedbackBody, children: body })] }), _jsx(Button, { type: 'button', variant: 'outline', size: 'large', dataTestId: dataTestId, className: styles.feedbackAction, onClick: onAction, children: actionLabel })] }));
    if (isOk) {
        return (_jsx("output", { className: panelClassName, "data-testid": regionTestId, "aria-live": 'polite', children: inner }));
    }
    return (_jsx("div", { className: panelClassName, "data-testid": regionTestId, role: 'alert', "aria-live": 'assertive', children: inner }));
}
export function InsideSalesContactRequestSection({ organizationId, isEmissorFlow = false, }) {
    const { t } = useTranslation();
    const [submitFeedback, setSubmitFeedback] = useState(null);
    const companyDocument = useAppSelector(selectSelectedCompanyDocument);
    const selectedAccount = useAppSelector(selectSelectedAccount);
    const { data: ownerContact } = useGetOrganizationOwnerContactQuery({ organizationId: organizationId ?? '' }, { skip: !organizationId });
    const [createLegacyContactRequest, { isLoading: isSubmittingLegacyContact }] = useCreateContactRequestMutation();
    const [createEmissorContactRequest, { isLoading: isSubmittingEmissorContact }] = useCreateEmissorContactRequestByDomain();
    const createContactRequest = isEmissorFlow ? createEmissorContactRequest : createLegacyContactRequest;
    const isSubmittingContact = isEmissorFlow ? isSubmittingEmissorContact : isSubmittingLegacyContact;
    const { register, control, handleSubmit, reset, formState: { errors, isValid }, } = useForm({
        mode: 'onChange',
        reValidateMode: 'onChange',
        defaultValues: EMPTY_FORM_VALUES,
    });
    const collaboratorsInvalidMsg = t('benefits.inside-sales.contact.collaboratorsInvalid');
    const requiredMsg = t('benefits.inside-sales.contact.fieldRequired');
    const emailInvalidMsg = t('benefits.inside-sales.contact.emailInvalid');
    const phoneInvalidMsg = t('benefits.inside-sales.contact.phoneInvalid');
    const stateRequiredMsg = t('benefits.inside-sales.contact.stateRequired');
    const fullNameField = register('fullName', { required: requiredMsg });
    const emailField = register('email', {
        required: requiredMsg,
        pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: emailInvalidMsg,
        },
    });
    const cityField = register('city', { required: requiredMsg });
    const collaboratorsField = register('collaboratorCount', {
        required: requiredMsg,
        validate: v => validateCollaboratorCountValue(v, collaboratorsInvalidMsg),
    });
    useEffect(() => {
        if (!ownerContact) {
            return;
        }
        reset(formValuesFromOwner(ownerContact));
    }, [ownerContact, reset]);
    const hasFormErrors = !isValid;
    const isSubmitDisabled = isSubmittingContact || hasFormErrors;
    const onSubmit = handleSubmit(async (values) => {
        setSubmitFeedback(null);
        try {
            const lead = {
                fullName: values.fullName.trim(),
                email: values.email.trim(),
                phone: normalizeContactPhoneDigits(values.phone),
                stateCode: (values.stateCode ?? '').trim(),
                city: values.city.trim(),
                collaboratorCount: Number.parseInt(values.collaboratorCount.trim(), 10),
            };
            const companyName = selectedAccount?.companyName;
            const payload = withOptionalCompanyIdentity(lead, companyDocument || companyName ? { document: companyDocument, name: companyName } : undefined);
            await createContactRequest(payload).unwrap();
            setSubmitFeedback('ok');
        }
        catch {
            setSubmitFeedback('err');
        }
    });
    const handleFeedbackSuccessAction = useCallback(() => {
        setSubmitFeedback(null);
        if (ownerContact) {
            reset(formValuesFromOwner(ownerContact));
        }
        else {
            reset(EMPTY_FORM_VALUES);
        }
    }, [ownerContact, reset]);
    const handleFeedbackTryAgain = useCallback(() => {
        setSubmitFeedback(null);
    }, []);
    const showForm = submitFeedback === null;
    return (_jsx("div", { className: styles.outer, "data-testid": 'viewInsideSalesContactSection', children: _jsxs("section", { className: styles.section, children: [showForm && _jsx(InsideSalesContactIntro, {}), showForm ? (_jsx(InsideSalesContactFormFields, { onSubmit: onSubmit, fullNameField: fullNameField, emailField: emailField, cityField: cityField, collaboratorsField: collaboratorsField, control: control, errors: errors, requiredMsg: requiredMsg, stateRequiredMsg: stateRequiredMsg, phoneInvalidMsg: phoneInvalidMsg, isSubmitDisabled: isSubmitDisabled, isSubmittingContact: isSubmittingContact })) : (_jsx(InsideSalesContactFeedbackPanel, { variant: submitFeedback, onAction: submitFeedback === 'ok' ? handleFeedbackSuccessAction : handleFeedbackTryAgain }))] }) }));
}
